/**
 * Keyboard Navigation Component
 * 
 * Provides enhanced keyboard navigation patterns and shortcuts
 * for improved accessibility and user experience.
 */

import React, { useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useARIALiveRegion } from './ARIAEnhancements'

interface KeyboardNavigationProps {
  /**
   * Enable global keyboard shortcuts
   */
  enableShortcuts?: boolean
  
  /**
   * Enable roving tabindex for complex widgets
   */
  enableRovingTabindex?: boolean
  
  /**
   * Enable escape key handling
   */
  enableEscapeKey?: boolean
  
  /**
   * Custom keyboard shortcuts
   */
  customShortcuts?: KeyboardShortcut[]
}

export interface KeyboardShortcut {
  key: string
  modifiers?: ('ctrl' | 'alt' | 'shift' | 'meta')[]
  action: () => void
  description: string
  category?: string
}

export interface FocusableElement {
  element: HTMLElement
  index: number
  isActive: boolean
}

export const KeyboardNavigation: React.FC<KeyboardNavigationProps> = ({
  enableShortcuts = true,
  enableRovingTabindex = true,
  enableEscapeKey = true,
  customShortcuts = []
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { announceInfo } = useARIALiveRegion()
  const helpDialogRef = useRef<HTMLDialogElement>(null)
  const [showHelp, setShowHelp] = React.useState(false)

  // Default keyboard shortcuts
  const defaultShortcuts: KeyboardShortcut[] = [
    {
      key: '?',
      modifiers: [],
      action: () => toggleKeyboardHelp(),
      description: 'Show keyboard shortcuts help',
      category: 'General'
    },
    {
      key: 'h',
      modifiers: ['alt'],
      action: () => navigate('/'),
      description: 'Go to home page',
      category: 'Navigation'
    },
    {
      key: 's',
      modifiers: ['alt'],
      action: () => focusSearch(),
      description: 'Focus search field',
      category: 'Navigation'
    },
    {
      key: 'm',
      modifiers: ['alt'],
      action: () => focusMainContent(),
      description: 'Focus main content',
      category: 'Navigation'
    },
    {
      key: 'n',
      modifiers: ['alt'],
      action: () => focusNavigation(),
      description: 'Focus navigation',
      category: 'Navigation'
    },
    {
      key: 'f',
      modifiers: ['alt'],
      action: () => focusFooter(),
      description: 'Focus footer',
      category: 'Navigation'
    },
    {
      key: 'l',
      modifiers: ['alt'],
      action: () => toggleLanguageSwitcher(),
      description: 'Toggle language switcher',
      category: 'Language'
    }
  ]

  const allShortcuts = [...defaultShortcuts, ...customShortcuts]

  // Toggle keyboard help dialog
  const toggleKeyboardHelp = useCallback(() => {
    setShowHelp(prev => {
      const newValue = !prev
      if (newValue) {
        announceInfo('Keyboard shortcuts help opened')
        setTimeout(() => {
          helpDialogRef.current?.showModal()
        }, 100)
      } else {
        helpDialogRef.current?.close()
        announceInfo('Keyboard shortcuts help closed')
      }
      return newValue
    })
  }, [announceInfo])

  // Focus management functions
  const focusSearch = useCallback(() => {
    const searchInput = document.querySelector('input[type="search"], [role="search"] input, #search-input, .search-input')
    if (searchInput) {
      (searchInput as HTMLElement).focus()
      announceInfo('Search field focused')
    } else {
      announceInfo('No search field found on this page')
    }
  }, [announceInfo])

  const focusMainContent = useCallback(() => {
    const main = document.querySelector('main, [role="main"], #main-content')
    if (main) {
      const mainElement = main as HTMLElement
      if (!mainElement.getAttribute('tabindex')) {
        mainElement.setAttribute('tabindex', '-1')
      }
      mainElement.focus()
      announceInfo('Main content focused')
    }
  }, [announceInfo])

  const focusNavigation = useCallback(() => {
    const nav = document.querySelector('nav, [role="navigation"], #main-navigation')
    if (nav) {
      const navElement = nav as HTMLElement
      // Focus first focusable element within navigation
      const focusable = nav.querySelector('a, button, [tabindex]:not([tabindex="-1"])')
      if (focusable) {
        (focusable as HTMLElement).focus()
        announceInfo('Navigation focused')
      } else {
        if (!navElement.getAttribute('tabindex')) {
          navElement.setAttribute('tabindex', '-1')
        }
        navElement.focus()
        announceInfo('Navigation area focused')
      }
    }
  }, [announceInfo])

  const focusFooter = useCallback(() => {
    const footer = document.querySelector('footer, [role="contentinfo"], #page-footer')
    if (footer) {
      const footerElement = footer as HTMLElement
      if (!footerElement.getAttribute('tabindex')) {
        footerElement.setAttribute('tabindex', '-1')
      }
      footerElement.focus()
      announceInfo('Footer focused')
    }
  }, [announceInfo])

  const toggleLanguageSwitcher = useCallback(() => {
    const langButton = document.querySelector('[data-language-switcher], .language-switcher button, .language-toggle')
    if (langButton) {
      (langButton as HTMLElement).click()
      announceInfo('Language switcher toggled')
    } else {
      announceInfo('Language switcher not found')
    }
  }, [announceInfo])

  // Keyboard event handler
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Skip if user is typing in an input field
    if (event.target instanceof HTMLInputElement || 
        event.target instanceof HTMLTextAreaElement || 
        event.target instanceof HTMLSelectElement ||
        (event.target as HTMLElement).contentEditable === 'true') {
      return
    }

    for (const shortcut of allShortcuts) {
      const modifiersMatch = shortcut.modifiers?.every(mod => {
        switch (mod) {
          case 'ctrl': return event.ctrlKey
          case 'alt': return event.altKey
          case 'shift': return event.shiftKey
          case 'meta': return event.metaKey
          default: return false
        }
      }) ?? true

      const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase()

      if (keyMatch && modifiersMatch) {
        event.preventDefault()
        shortcut.action()
        break
      }
    }
  }, [allShortcuts])

  // Escape key handler
  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key !== 'Escape') return

    const activeElement = document.activeElement as HTMLElement

    // Close modals, dropdowns, etc.
    if (showHelp) {
      toggleKeyboardHelp()
      return
    }

    // Close open dropdowns
    const openDropdown = document.querySelector('[aria-expanded="true"]')
    if (openDropdown) {
      openDropdown.setAttribute('aria-expanded', 'false');
      (openDropdown as HTMLElement).focus()
      announceInfo('Dropdown closed')
      return
    }

    // Close modal dialogs
    const openDialog = document.querySelector('dialog[open]')
    if (openDialog && openDialog !== helpDialogRef.current) {
      (openDialog as HTMLDialogElement).close()
      announceInfo('Dialog closed')
      return
    }

    // Remove focus from certain elements
    if (activeElement) {
      const shouldBlur = activeElement.matches('.dropdown-menu *, .modal *, [role="tooltip"] *')
      if (shouldBlur) {
        activeElement.blur()
        announceInfo('Focus cleared')
      }
    }
  }, [showHelp, toggleKeyboardHelp, announceInfo])

  // Set up roving tabindex for complex widgets
  const setupRovingTabindex = useCallback(() => {
    const widgets = document.querySelectorAll('[role="tablist"], [role="menubar"], [role="grid"], [role="listbox"]')
    
    widgets.forEach(widget => {
      const items = widget.querySelectorAll('[role="tab"], [role="menuitem"], [role="gridcell"], [role="option"]')
      
      if (items.length === 0) return

      // Set initial tabindex
      Array.from(items).forEach((item, index) => {
        item.setAttribute('tabindex', index === 0 ? '0' : '-1')
      })

      // Handle arrow key navigation
      const handleArrowKeys = (event: Event) => {
        const keyboardEvent = event as KeyboardEvent
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(keyboardEvent.key)) return

        event.preventDefault()
        
        const currentIndex = Array.from(items).indexOf(event.target as Element)
        let newIndex: number

        if (keyboardEvent.key === 'ArrowRight' || keyboardEvent.key === 'ArrowDown') {
          newIndex = (currentIndex + 1) % items.length
        } else {
          newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1
        }

        // Update tabindex
        for (let index = 0; index < items.length; index++) {
          const item = items[index] as HTMLElement
          item.setAttribute('tabindex', index === newIndex ? '0' : '-1')
        }

        // Focus new item
        (items[newIndex] as HTMLElement).focus()
      }

      widget.addEventListener('keydown', handleArrowKeys)
    })
  }, [])

  // Set up keyboard navigation
  useEffect(() => {
    if (enableShortcuts) {
      document.addEventListener('keydown', handleKeyDown)
    }

    if (enableEscapeKey) {
      document.addEventListener('keydown', handleEscapeKey)
    }

    if (enableRovingTabindex) {
      setupRovingTabindex()
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [handleKeyDown, handleEscapeKey, enableShortcuts, enableEscapeKey, enableRovingTabindex, setupRovingTabindex])

  // Update roving tabindex on route changes
  useEffect(() => {
    if (enableRovingTabindex) {
      setTimeout(() => setupRovingTabindex(), 100)
    }
  }, [location.pathname, enableRovingTabindex, setupRovingTabindex])

  // Group shortcuts by category
  const shortcutsByCategory = allShortcuts.reduce((acc, shortcut) => {
    const category = shortcut.category || 'Other'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(shortcut)
    return acc
  }, {} as Record<string, KeyboardShortcut[]>)

  // Format shortcut keys for display
  const formatShortcut = (shortcut: KeyboardShortcut) => {
    const parts = []
    if (shortcut.modifiers?.includes('ctrl')) parts.push('Ctrl')
    if (shortcut.modifiers?.includes('alt')) parts.push('Alt')
    if (shortcut.modifiers?.includes('shift')) parts.push('Shift')
    if (shortcut.modifiers?.includes('meta')) parts.push('Cmd')
    parts.push(shortcut.key.toUpperCase())
    return parts.join(' + ')
  }

  return (
    <>
      {/* Keyboard Help Dialog */}
      <dialog
        ref={helpDialogRef}
        className="p-0 rounded-lg shadow-xl backdrop:bg-black backdrop:bg-opacity-50 max-w-2xl w-full max-h-[80vh] overflow-auto"
        onClose={() => setShowHelp(false)}
      >
        <div className="bg-white p-6">
          <header className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Keyboard Shortcuts</h2>
            <button
              onClick={toggleKeyboardHelp}
              className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              aria-label="Close keyboard shortcuts help"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div className="space-y-6">
            {Object.entries(shortcutsByCategory).map(([category, shortcuts]) => (
              <div key={category}>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {category}
                </h3>
                <dl className="space-y-2">
                  {shortcuts.map((shortcut, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <dt className="text-gray-600">{shortcut.description}</dt>
                      <dd className="font-mono text-sm bg-gray-100 px-2 py-1 rounded border">
                        {formatShortcut(shortcut)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>

          <footer className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-600">
            <p>Press <kbd className="font-mono bg-gray-100 px-1 py-0.5 rounded border">Escape</kbd> to close this dialog.</p>
          </footer>
        </div>
      </dialog>

      {/* Screen reader instructions */}
      <div className="sr-only">
        <h2>Keyboard Navigation Available</h2>
        <p>This page supports keyboard navigation shortcuts. Press ? to see all available shortcuts.</p>
        <p>Use Tab to navigate between interactive elements, and Enter or Space to activate them.</p>
      </div>
    </>
  )
}

/**
 * Hook for registering custom keyboard shortcuts
 */
export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[]) => {
  const [registeredShortcuts, setRegisteredShortcuts] = React.useState<KeyboardShortcut[]>(shortcuts)

  const addShortcut = useCallback((shortcut: KeyboardShortcut) => {
    setRegisteredShortcuts(prev => [...prev, shortcut])
  }, [])

  const removeShortcut = useCallback((key: string, modifiers?: string[]) => {
    setRegisteredShortcuts(prev => 
      prev.filter(s => s.key !== key || JSON.stringify(s.modifiers) !== JSON.stringify(modifiers))
    )
  }, [])

  const clearShortcuts = useCallback(() => {
    setRegisteredShortcuts([])
  }, [])

  return {
    shortcuts: registeredShortcuts,
    addShortcut,
    removeShortcut,
    clearShortcuts
  }
}

export default KeyboardNavigation